import { useEffect, useRef, useState } from 'react';
import { ErreurApi } from '../api/client';
import { useAttente } from '../api/hooks';
import { formatCourt } from '../format';
import { useToasts } from './Toasts';

/**
 * « Repousser » : la commande patiente dans « En cours » jusqu'à un jour.
 *
 * Remplace le bloc « Mettre en attente » du bas de fiche. On repousse au moment
 * où l'on apprend que rien ne bougera avant telle date — en relançant, en
 * saisissant, en relisant un mail —, donc depuis la fenêtre ouverte à ce
 * moment-là, sans aller chercher un formulaire ailleurs.
 *
 * La date, et ce qu'on attend — facultatif, mais c'est ce qui permet de
 * relire le report sans rouvrir la commande : la liste le rend en « Attente
 * de … », la fiche à côté de la date. Le jour venu, la commande revient
 * d'elle-même dans « À faire ».
 *
 * **Repoussée, la commande montre « Reprendre », et seulement lui.** Reprendre
 * annule le report : la commande retourne aussitôt dans le travail courant, et
 * le bouton redevient « Repousser » — on peut donc repousser de nouveau, à une
 * autre date, sans chemin à part.
 *
 * Rien sur une commande livrée : il n'y a plus rien à repousser.
 */
export function Repousser({
  commande, apres, serre = false,
}: {
  commande: { id: string; ref: string; livre: boolean; attenteJusquAu: string | null; attenteMotif?: string | null };
  /** Appelé une fois la commande repoussée ou reprise — pour fermer la fenêtre hôte. */
  apres?: () => void;
  /** Suivi d'un autre bouton déjà calé à gauche : ne pas repousser le reste. */
  serre?: boolean;
}) {
  const { ajouter } = useToasts();
  const { poser, lever } = useAttente();
  const [ouvert, setOuvert] = useState(false);
  const [jusquAu, setJusquAu] = useState('');
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const zone = useRef<HTMLSpanElement>(null);
  /*
   * L'état affiché suit la réponse de l'API, pas seulement la commande reçue :
   * les fenêtres gardent la commande telle qu'à leur ouverture, et le bouton
   * ne basculerait pas avant leur fermeture.
   */
  const [repousseeJusquAu, setRepousseeJusquAu] = useState(commande.attenteJusquAu);
  useEffect(() => setRepousseeJusquAu(commande.attenteJusquAu), [commande.attenteJusquAu]);

  // Un clic hors du menu le referme, sans fermer la fenêtre qui le porte.
  useEffect(() => {
    if (!ouvert) return;
    const dehors = (e: MouseEvent) => {
      if (zone.current && !zone.current.contains(e.target as Node)) setOuvert(false);
    };
    document.addEventListener('mousedown', dehors);
    return () => document.removeEventListener('mousedown', dehors);
  }, [ouvert]);

  if (commande.livre) return null;

  const demain = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
  const occupe = poser.isPending || lever.isPending;

  const repousser = async () => {
    setErreur(null);
    try {
      const note = motif.trim();
      const maj = await poser.mutateAsync({ id: commande.id, jusquAu, ...(note ? { motif: note } : {}) });
      setRepousseeJusquAu(maj.attenteJusquAu ?? jusquAu);
      ajouter(`${commande.ref} repoussée au ${formatCourt(jusquAu)}${note ? ` (${note})` : ''} : elle patiente dans « En cours ».`);
      setOuvert(false);
      setJusquAu('');
      setMotif('');
      apres?.();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "La commande n'a pas pu être repoussée.");
    }
  };

  const reprendre = async () => {
    try {
      await lever.mutateAsync(commande.id);
      setRepousseeJusquAu(null);
      ajouter(`${commande.ref} reprise : le report est annulé.`);
      apres?.();
    } catch (e) {
      ajouter(e instanceof ErreurApi ? e.message : "La commande n'a pas pu être reprise.", 'bad');
    }
  };

  if (repousseeJusquAu) {
    return (
      <span className={`repousser${serre ? ' serre' : ''}`}>
        <button
          type="button" className="btn" disabled={occupe}
          title={`Repoussée jusqu'au ${formatCourt(repousseeJusquAu)}`
            + `${commande.attenteMotif ? ` — ${commande.attenteMotif}` : ''}. Reprendre annule le report.`}
          onClick={() => void reprendre()}
        >
          {lever.isPending ? <><span className="spin" /> …</> : 'Reprendre'}
        </button>
      </span>
    );
  }

  return (
    <span
      ref={zone} className={`repousser${serre ? ' serre' : ''}`}
      // Échap ferme le menu seul : la fenêtre qui le porte écoute aussi la
      // touche, et la refermer ferait perdre ce qu'on y avait saisi.
      onKeyDown={(e) => {
        if (e.key === 'Escape' && ouvert) { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); setOuvert(false); }
      }}
    >
      <button
        type="button" className="btn" aria-expanded={ouvert} disabled={occupe}
        onClick={() => { setOuvert((o) => !o); setErreur(null); }}
      >
        Repousser
      </button>
      {ouvert && (
        <div className="repousser-menu" role="dialog" aria-label={`Repousser ${commande.ref}`}>
          <div className="field" style={{ marginBottom: 8 }}>
            <label htmlFor={`rep-${commande.id}`}>Patienter jusqu&apos;au</label>
            <input
              id={`rep-${commande.id}`} type="date" min={demain} value={jusquAu} autoFocus
              onChange={(e) => setJusquAu(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && jusquAu) { e.preventDefault(); void repousser(); } }}
            />
          </div>
          <div className="field" style={{ marginBottom: 8 }}>
            <label htmlFor={`rep-motif-${commande.id}`}>Ce qu&apos;on attend <span className="sub">(facultatif)</span></label>
            <input
              id={`rep-motif-${commande.id}`} type="text" maxLength={280} value={motif}
              placeholder="ex. réassort fournisseur, retour du client"
              onChange={(e) => setMotif(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && jusquAu) { e.preventDefault(); void repousser(); } }}
            />
          </div>
          {erreur && <p className="err" role="alert" style={{ margin: '0 0 8px' }}>{erreur}</p>}
          <div>
            <button
              type="button" className="btn pri sm" disabled={occupe || !jusquAu}
              onClick={() => void repousser()}
            >
              {poser.isPending ? <><span className="spin" /> …</> : 'Repousser'}
            </button>
          </div>
        </div>
      )}
    </span>
  );
}
